import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Clock, ExternalLink, MapPin, Share2 } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";
import { toWordPreview } from "@/lib/textPreview";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { buildShareUrl } from "@/lib/shareUrl";
import { shareContent } from "@/lib/shareContent";

const fontFamilyByStyle: Record<string, string> = {
  normal: "'Inter', system-ui, sans-serif",
  times_new_roman: "'Times New Roman', Times, serif",
  courier_new: "'Courier New', Courier, monospace",
};

const JobsDetail = () => {
  const { id } = useParams();
  const { jobs } = useData();
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  // Find job by slug first, then by ID for backward compatibility
  const item = jobs.find((job) => job.slug === id) || jobs.find((job) => job.id === id);

  const safeDescription = sanitizeRichTextHtml(item?.description || "");
  const textAlign = (item?.contentAlignment || "justify") as CSSProperties["textAlign"];
  const fontFamily = fontFamilyByStyle[item?.fontStyle || "times_new_roman"] || fontFamilyByStyle.times_new_roman;
  const contentStyle: CSSProperties = { textAlign, fontFamily };

  useEffect(() => {
    const faviconHref = item?.imageUrl ? String(item.imageUrl).trim() : "data:,";
    const selectors = 'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]';
    const existingLinks = Array.from(document.head.querySelectorAll<HTMLLinkElement>(selectors));
    const snapshots = existingLinks.map((el) => ({
      el,
      href: el.getAttribute("href"),
      rel: el.getAttribute("rel"),
      type: el.getAttribute("type"),
      sizes: el.getAttribute("sizes"),
    }));
    const createdLinks: HTMLLinkElement[] = [];
    const targets = existingLinks.length > 0 ? existingLinks : (() => {
      const link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
      createdLinks.push(link);
      return [link];
    })();

    targets.forEach((linkEl) => {
      linkEl.setAttribute("href", faviconHref);
      const rel = String(linkEl.getAttribute("rel") || "").toLowerCase();
      if (rel === "icon" && !linkEl.getAttribute("sizes")) {
        linkEl.setAttribute("sizes", "any");
      }
    });

    return () => {
      snapshots.forEach(({ el, href, rel, type, sizes }) => {
        if (href === null) {
          el.removeAttribute("href");
        } else {
          el.setAttribute("href", href);
        }

        if (rel === null) {
          el.removeAttribute("rel");
        } else {
          el.setAttribute("rel", rel);
        }

        if (type === null) {
          el.removeAttribute("type");
        } else {
          el.setAttribute("type", type);
        }

        if (sizes === null) {
          el.removeAttribute("sizes");
        } else {
          el.setAttribute("sizes", sizes);
        }
      });

      createdLinks.forEach((el) => el.remove());
    };
  }, [item?.imageUrl]);

  const handleShare = async () => {
    if (!item) {
      return;
    }

    setIsSharing(true);
    setShareMessage("");

    const shareUrl = buildShareUrl("jobs", item.slug || item.id);
    const result = await shareContent({
      title: `${item.title} - ${item.company}`,
      text: toWordPreview(item.description, 20),
      url: shareUrl,
      imageUrl: item.imageUrl,
    });

    if (result.status === "copied") {
      setShareMessage("Job link copied to clipboard.");
    } else if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
    } else if (result.status === "error") {
      setShareMessage("Unable to share this job right now.");
    }

    setIsSharing(false);
  };

  if (!item) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">Job details not found.</p>
        <Link to="/jobs" className="text-secondary hover:underline mt-4 inline-block">
          {"<- Back to Jobs"}
        </Link>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" style={contentStyle}>
          <ArrowLeft size={16} /> Back to Jobs
        </Link>

        {item.imageUrl && (
          <div className="mb-6">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-64 md:h-80 object-cover rounded-lg border border-border"
              onError={(event) => {
                event.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        )}

        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3 mt-1" style={contentStyle}>
          {item.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="badge-success">{item.type}</span>
          <span className="badge-info">{item.category}</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground mb-6" style={contentStyle}>
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1">
              <Briefcase size={14} /> {item.company}
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={14} /> {item.location}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} /> {formatDisplayDate(item.postedDate)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void handleShare()}
            disabled={isSharing}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-input bg-background text-foreground hover:bg-muted disabled:opacity-60"
          >
            <Share2 size={14} />
            {isSharing ? "Sharing..." : "Share"}
          </button>
        </div>

        <div className="mb-6">
          <p className="text-lg font-semibold text-foreground">{item.salary}</p>
        </div>

        {item.videoUrl && (
          <div className="mb-6">
            <YouTubeEmbed url={item.videoUrl} title={item.title} />
          </div>
        )}

        <div className="prose prose-sm max-w-none text-foreground" style={contentStyle}>
          <div className="news-richtext text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: safeDescription }} />
        </div>

        <div className="mt-6">
          <a
            href={item.applyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
          >
            Apply Now <ExternalLink size={14} />
          </a>
        </div>

        {shareMessage && <p className="text-xs text-muted-foreground mt-4">{shareMessage}</p>}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border" style={contentStyle}>
          <p className="text-xs text-muted-foreground">
            <strong>Disclaimer:</strong> Job listings are informational only. Verify employer and terms directly before applying.
          </p>
        </div>
      </div>
    </div>
  );
};

export default JobsDetail;
